import { Box, Divider, HStack, Tag, Text, VStack } from '@chakra-ui/react';
import { TransactionNotificationLogFragment } from '@client/graphql/__generated__/types';
import dayjs from 'dayjs';
import * as DOMPurify from 'isomorphic-dompurify';
import { FC, useMemo } from 'react';
import { contactTypeLabels } from './contactTypeLabels';

interface EmailPreviewProps {
  notification: TransactionNotificationLogFragment;
}

export const EmailPreview: FC<EmailPreviewProps> = ({ notification }) => {
  const template = notification.notificationTemplate;
  const sentAt = notification.sentAt as string;

  // Process content - sanitize HTML to prevent XSS attacks
  const processedContent = useMemo(() => {
    const isHtml = /<[a-z][\s\S]*>/i.test(notification.messageContent);
    if (isHtml) {
      // Sanitize HTML content using DOMPurify with extended allowed tags
      return DOMPurify.sanitize(notification.messageContent, {
        ALLOWED_TAGS: [
          'p',
          'b',
          'i',
          'u',
          'br',
          'strong',
          'em',
          'ul',
          'ol',
          'li',
          'a',
          'span',
          'div',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
        ],
        ALLOWED_ATTR: ['href', 'style', 'class', 'target'],
      });
    }

    // For plain text, escape HTML entities and wrap in paragraphs
    const escapeHtml = (text: string): string => {
      const div = document.createElement('div');
      div.textContent = text;

      return div.innerHTML;
    };

    // Convert plain text to HTML with line breaks
    return notification.messageContent
      .split('\n')
      .map((line) => `<p>${escapeHtml(line)}</p>`)
      .join('');
  }, [notification.messageContent]);

  return (
    <VStack alignItems="stretch" spacing={4}>
      {/* Email header */}
      <VStack alignItems="flex-start" spacing={2}>
        <HStack>
          <Text color="whiteAlpha.500" fontSize="sm" minW="60px">
            Subject:
          </Text>
          <Text color="white" fontSize="sm" fontWeight="medium">
            {notification.title || template?.title || 'No Subject'}
          </Text>
        </HStack>

        <HStack alignItems="flex-start">
          {notification.recipients.length > 0 && (
            <Text color="whiteAlpha.500" fontSize="sm" minW="60px">
              To:
            </Text>
          )}
          <VStack alignItems="flex-start" spacing={1}>
            {notification.recipients.map((recipient) => (
              <HStack key={recipient.id} spacing={2}>
                <Text color="white" fontSize="sm">
                  {recipient.firstName} {recipient.lastName}
                </Text>
                <Tag
                  bg="whiteAlpha.100"
                  color="whiteAlpha.600"
                  fontSize="xs"
                  size="sm"
                >
                  {contactTypeLabels[recipient.contactType] ||
                    recipient.contactType}
                </Tag>
                {recipient.email && (
                  <Text color="whiteAlpha.500" fontSize="sm">
                    &lt;{recipient.email}&gt;
                  </Text>
                )}
              </HStack>
            ))}
          </VStack>
        </HStack>
      </VStack>

      <Divider borderColor="whiteAlpha.200" />

      {/* Email body */}
      <Box
        borderRadius="md"
        maxH="400px"
        overflowY="auto"
        p={4}
        sx={{
          '& p': { mb: 2 },
          '& a': { color: 'blue.500', textDecoration: 'underline' },
          '& ul, & ol': { pl: 4, mb: 2 },
          '& li': { mb: 1 },
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: processedContent }} />
      </Box>

      {/* Timestamp */}
      {sentAt && (
        <Box>
          <Text color="whiteAlpha.500" fontSize="xs" mb={1}>
            SENT AT
          </Text>
          <Text color="white" fontSize="sm">
            {dayjs(sentAt).format('dddd, MMMM D, YYYY [at] h:mm A')}
          </Text>
        </Box>
      )}
    </VStack>
  );
};
